import { useDisclosure, useToast } from "@chakra-ui/react";
import * as React from "react";
import { useCallback, useState } from "react";
import { useQueryClient } from "react-query";
import { useDispatch } from "react-redux";
import {
  purchasedGoodsWaterUsageKeys,
  useAddPurchasedGoodsWaterUsageItems,
  useDeletePurchasedGoodsWaterUsageItem,
  useDownloadPurchasedGoodsWaterUsageTemplate,
  useGetPurchasedGoodsWaterUsageItems,
  useImportPurchasedGoodsWaterUsageItems,
  useUpdatePurchasedGoodsWaterUsageItem
} from "../../../../../../hooks/query";
import { showErrorToast, showSuccessToast } from "../../../../../../utils";
import { hideAppSpinner, showAppSpinner } from "../../../../../../context/Store/slices/appSpinnerSlice";
import { CategoryGrid } from "../CategoryGrid";
import { DeleteDialog } from "../../../../../DataEntryPlanning/components/DeleteDialog";
import { WaterUsageModalWrapper } from "../CategoryModalWrappers";
import { WATER_MEASURE_OPTIONS, DEFAULT_MEASURE_ID } from "../CategoryModalWrappers/WaterUsageModalWrapper";
import { buildDimensionFormDefaults, withDimensionItems } from "./helpers";

const FORM_INPUT_KEY = "waterUsageItems";

function WaterUsageGrid({
  questionnaireId,
  organizationStructure,
  dimensionItems,
  isPreview,
  tabKey
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  const [selectedItem, setSelectedItem] = useState(null);
  const [categoryModalData, setCategoryModalData] = useState(null);

  const {
    isOpen: isCategoryModalOpen,
    onOpen: onCategoryModalOpen,
    onClose: onCategoryModalClose
  } = useDisclosure();

  const {
    isOpen: isDeleteItemDialogOpen,
    onOpen: onDeleteItemDialogOpen,
    onClose: onDeleteItemDialogClose
  } = useDisclosure();

  const colHeaders = ["Water Usage", "Unit"];
  const attributesOrder = ["value", "measureLabel"];

  const prepareSectionValues = useCallback(items => {
    return items.map(item => ({
      id: item.id ?? null,
      dimensionItems: item.dimensionItems ?? null,
      dimensionOneItem: item.dimensionItems?.dimensionOneItem ?? null,
      dimensionTwoItem: item.dimensionItems?.dimensionTwoItem ?? null,
      dimensionThreeItem: item.dimensionItems?.dimensionThreeItem ?? null,
      dimensionFourItem: item.dimensionItems?.dimensionFourItem ?? null,
      dimensionFiveItem: item.dimensionItems?.dimensionFiveItem ?? null,
      value: item.value ?? "",
      measureId: item.measureId ?? null,
      measureLabel: WATER_MEASURE_OPTIONS.find(opt => Number(opt.value) === Number(item.measureId))?.label
        ?? (item.measureId != null ? String(item.measureId) : "")
    }));
  }, []);

  const handleAddItem = () => {
    setCategoryModalData({
      fieldArrayName: FORM_INPUT_KEY,
      columnHeaders: [...colHeaders, ""],
      modalHeaderItemsType: "Water Usage",
      organizationStructure,
      dimensionItems
    });
    onCategoryModalOpen();
  };

  const handleEditItem = (item) => {
    setCategoryModalData({
      fieldArrayName: FORM_INPUT_KEY,
      editData: {
        id: item.id,
        dimensionItems: item.dimensionItems ?? null,
        value: item.value,
        measureId: item.measureId
      },
      columnHeaders: colHeaders,
      modalHeaderItemsType: "Water Usage",
      organizationStructure,
      dimensionItems
    });
    onCategoryModalOpen();
  };

  const handleDeleteItem = (item) => {
    onDeleteItemDialogOpen();
    setSelectedItem({ id: item.id });
  };

  const handleDeleteItemDialogClose = () => {
    setSelectedItem(null);
    onDeleteItemDialogClose();
  };

  const handleCategoryModalClose = () => {
    setCategoryModalData(null);
    onCategoryModalClose();
  };

  const { mutate: addItem } = useAddPurchasedGoodsWaterUsageItems({
    onSuccess: () => {
      handleCategoryModalClose();
      showSuccessToast(toast, "Water usage rows added");
      queryClient.invalidateQueries(purchasedGoodsWaterUsageKeys.items());
    },
    onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    onSettled: () => dispatch(hideAppSpinner())
  });

  const { mutate: updateItem } = useUpdatePurchasedGoodsWaterUsageItem({
    onSuccess: () => {
      handleCategoryModalClose();
      showSuccessToast(toast, "Water usage row updated");
      queryClient.invalidateQueries(purchasedGoodsWaterUsageKeys.items());
    },
    onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    onSettled: () => dispatch(hideAppSpinner())
  });

  const { mutate: deleteItem } = useDeletePurchasedGoodsWaterUsageItem({
    onSuccess: () => {
      handleDeleteItemDialogClose();
      showSuccessToast(toast, "Water usage row deleted");
      queryClient.invalidateQueries(purchasedGoodsWaterUsageKeys.items());
    },
    onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    onSettled: () => dispatch(hideAppSpinner())
  });

  const { mutate: downloadTemplate } = useDownloadPurchasedGoodsWaterUsageTemplate({
    onMutate: () => dispatch(showAppSpinner()),
    onError: err => {
      const parsed = err.response?.data
        ? JSON.parse(String.fromCharCode.apply(null, new Int8Array(err.response.data)))
        : null;
      showErrorToast(toast, parsed?.message ?? err.message);
    },
    onSettled: () => dispatch(hideAppSpinner())
  });

  const { mutate: importItems } = useImportPurchasedGoodsWaterUsageItems({
    onSuccess: () => {
      showSuccessToast(toast, "Water usage rows imported");
      queryClient.invalidateQueries(purchasedGoodsWaterUsageKeys.items());
    },
    onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    onSettled: () => dispatch(hideAppSpinner())
  });

  const handleImport = (e) => {
    if (!e.target.files?.[0]) return;
    const formData = new FormData();
    formData.append("file", e.target.files[0]);
    dispatch(showAppSpinner());
    importItems({ questionnaireId, data: formData });
  };

  const deleteRecord = () => {
    dispatch(showAppSpinner());
    deleteItem({ questionnaireId, recordId: selectedItem.id });
  };

  const handleCategoryModalSubmit = (formData) => {
    const apiData = formData[categoryModalData.fieldArrayName].map(el => withDimensionItems({
      ...el,
      value: el.value !== "" ? Number(el.value) : null,
      measureId: el.measureId != null && el.measureId !== "" ? Number(el.measureId) : null
    }));

    dispatch(showAppSpinner());
    if (categoryModalData.editData) {
      updateItem({ questionnaireId, recordId: apiData[0].id, data: apiData[0] });
    } else {
      addItem({ questionnaireId, data: apiData });
    }
  };

  const getEmptyRow = () => ({
    value: "",
    measureId: DEFAULT_MEASURE_ID,
    ...buildDimensionFormDefaults(organizationStructure, dimensionItems)
  });

  return (
    <>
      <CategoryGrid
        questionnaireId={questionnaireId}
        columnHeaders={colHeaders}
        attributesOrder={attributesOrder}
        prepareSectionValues={prepareSectionValues}
        handleAddItem={handleAddItem}
        handleEditItem={handleEditItem}
        handleDeleteItem={handleDeleteItem}
        getItemsFunction={useGetPurchasedGoodsWaterUsageItems}
        isPreview={isPreview}
        organizationStructure={organizationStructure}
        onDownloadTemplate={() => downloadTemplate({ questionnaireId })}
        onImport={handleImport}
        importBtnId={`import-water-usage-${tabKey ?? "btn"}`}
        totalsConfig={{ valueKey: "value" }}
      />

      <DeleteDialog
        isOpen={isDeleteItemDialogOpen}
        onClose={handleDeleteItemDialogClose}
        onDelete={deleteRecord}
        entityName={"Item"}
      />

      {isCategoryModalOpen && categoryModalData !== null && (
        <WaterUsageModalWrapper
          modalData={categoryModalData}
          attributesOrder={attributesOrder}
          isOpen={isCategoryModalOpen}
          onClose={handleCategoryModalClose}
          onSubmit={handleCategoryModalSubmit}
          getEmptyRow={getEmptyRow}
        />
      )}
    </>
  );
}

export default WaterUsageGrid;
